import { Injectable } from '@angular/core';
import { DropdownListingInterface } from '@app/finxone-web-frontend/app/Modules/shared/widgets/accounts-context-dropdown-widget/accounts-context-dropdown-widget.component';
import {
  AccountStatusType,
  AccountTypes,
  SavingsAccountInfoType,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import {
  GetSavingAccount,
  UpdateInternalTransferFromAccountId,
  UpdateInternalTransferToAccountId,
} from '../../../actions/savings-account.action';
import { Currency } from '../../../dtos/currencies.dto';
import { SavingsAccountState, SavingsAccountStateModel } from '../../../state/savings-account.state';
import { getCurrencyDataByKey } from '../../../utils/utils';
import { AccountListingStrategyInterface } from '../../interfaces/account-listing-strategy.type';

@Injectable()
export class SavingsInternalTransferFormAccountListStrategy implements AccountListingStrategyInterface {
  @Select(SavingsAccountState.getAccounts)
  private accountState$: Observable<SavingsAccountStateModel>;
  private accountState: SavingsAccountStateModel;
  private accounts: SavingsAccountInfoType[];
  private selectedAccount: DropdownListingInterface;
  private dropdownListing: DropdownListingInterface[];

  public setupStateSubscription(
    store: Store,
    pageNumber: number,
    pageSize: number,
    accountContextStatusList: AccountStatusType[],
  ): Observable<any> {
    store.dispatch(new GetSavingAccount(pageNumber, pageSize, accountContextStatusList));

    return this.getAccountsStream();
  }

  public getAccountsStream(): Observable<any> {
    return this.accountState$;
  }

  public getAccounts(response: SavingsAccountStateModel) {
    this.accountState = response;
    this.accounts = response.accounts;
    return this.accounts;
  }

  public getDropdownListing(
    response: SavingsAccountStateModel,
    store: Store,
    currencies?: Currency[],
    attri?: UiZoneWidgetAttributeConfig,
  ) {
    this.dropdownListing = [];
    const listOfAccounts: DropdownListingInterface[] = [];
    response.accounts.forEach((element: SavingsAccountInfoType, index: number) => {
      const account = this.getSavingAccountData(element, index, currencies);
      listOfAccounts.push(account);
    });

    //Supported old method until migration is done
    if (
      attri?.['showAllOpenEasyAccessAccounts'] &&
      attri?.['context'] === AccountTypes.OPEN_EASY_ACCESS_SAVINGS_ACCOUNT
    ) {
      if (attri?.['showAllOpenEasyAccessAccounts'] === true) {
        attri['savingsInternalTransferAccountContext'] = 'from';
      } else {
        attri['savingsInternalTransferAccountContext'] = 'to';
      }
    }

    this.dropdownListing = listOfAccounts;
    this.filterAvailableAccountsAndSetSelectedValue(
      response,
      attri?.['savingsInternalTransferAccountContext'] === 'to' ? true : false,
    );
    return this.dropdownListing;
  }

  public getSelectedAccountValueForDropdown(
    store: Store,
    attri: UiZoneWidgetAttributeConfig,
  ): DropdownListingInterface {
    this.selectedAccount = this.getSelectedAccountByShowAllFlag(
      this.accountState,
      store,
      attri?.['savingsInternalTransferAccountContext'] === 'from' ? true : false,
    );
    return this.selectedAccount;
  }

  public getCurrentMultiCurrencyCode(store: Store) {
    return '';
  }

  public selectAccount(
    account: DropdownListingInterface,
    store: Store,
    attri: UiZoneWidgetAttributeConfig,
  ): void {
    this.selectedAccount = account;
    this.setInternalTransferAccounts(store, account, attri);
  }

  public fetchNewAccounts(
    store: Store,
    pageNumber: number,
    pageSize: number,
    accountContextStatusList: AccountStatusType[],
  ): void {
    store.dispatch(new GetSavingAccount(pageNumber, pageSize, accountContextStatusList));
  }

  public clearData(): void {
    this.accounts = [];
    this.selectedAccount = {} as DropdownListingInterface;
    this.dropdownListing = [];
  }

  public requiresFullAccountList(): boolean {
    return true;
  }

  private getSavingAccountData(
    accountInfo: SavingsAccountInfoType,
    index: number,
    currencies?: Currency[],
  ): DropdownListingInterface {
    return {
      accountCategoryType: 'savings',
      currencyCode: accountInfo.currency,
      currency: accountInfo.currency,
      name: accountInfo.accountName ?? '',
      status: accountInfo.status ?? '',
      country: accountInfo.country ?? '',
      balance: accountInfo.balance,
      accountName: accountInfo.accountName ?? '',
      accountId: accountInfo.accountId,
      totalBalance: accountInfo.balance,
      indicativeBalance: accountInfo.balance,
      availableBalance: accountInfo?.availableBalance ?? '',
      accountBalance: accountInfo?.balance ?? '',
      sortCode: accountInfo.accountIdentifier[0].sortCode ?? '',
      accountCreatedDate: accountInfo?.startDate ?? '',
      currencyFullName:
        currencies?.find((currency: Currency) => currency.currency_code === accountInfo?.currency)
          ?.currency_name ?? 'Unknown Currency',
      ledgerBalance: accountInfo?.balance ?? '',
      ledgerBalanceIncludingPendingExchange: accountInfo?.balance ?? '',
      alias: accountInfo?.accountName ?? '',
      accountNumber: accountInfo.accountIdentifier[0].accountNumber,
      bic: accountInfo.accountIdentifier[0].bic,
      ledgerNumber: accountInfo.accountIdentifier[0].ledgerNumber,
      iban: accountInfo.accountIdentifier[0].iban,
      providerExtraInfo: accountInfo.accountIdentifier[0].providerExtraInfo,
      achAccountNumber:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (accountInfo?.accountIdentifier[0]?.providerExtraInfo as any)?.ach?.accountNumber ?? '',
      achNumber:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (accountInfo?.accountIdentifier[0]?.providerExtraInfo as any)?.ach?.routingCodes?.aba ?? '',
      abaAccountNumber:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (accountInfo?.accountIdentifier[0]?.providerExtraInfo as any)?.wire?.accountNumber ?? '',
      abaNumber:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (accountInfo?.accountIdentifier[0]?.providerExtraInfo as any)?.wire?.routingCodes?.aba ?? '',
      accountProviderExtraInfo: accountInfo?.accountIdentifier[0]?.providerExtraInfo ?? {},
      pendingExchangeNetTotal: 0,
      pendingExchangeCount: 0,
      pendingTransactionNetTotal: 0,
      pendingTransactionCount: 0,
      defaultMarker: index === 0 ? '(Default)' : '',
      currencyName: getCurrencyDataByKey('currency_name', accountInfo, currencies ?? []),
      countryCode: accountInfo.country,
      accountStatus: `${accountInfo?.status?.charAt(0)?.toUpperCase()}${accountInfo?.status?.slice(1)}`,
      accountType: accountInfo.accountType,
      isFundingEnabled: accountInfo.isFundingEnabled ?? false,
      isWithdrawalEnabled: accountInfo.isWithdrawalEnabled ?? false,
      maxDeposit: accountInfo.maxDeposit ?? '',
      interestRate: accountInfo?.interestRate ?? null,
      annualEquivalantInterestRate: accountInfo?.annualEquivalantInterestRate ?? null,
    };
  }

  private getCurrencyDataByKey(valueKey: string, currency: string, currencies?: Currency[]) {
    const currencyObject = currencies?.find((v) => v.currency_code === currency);
    return currencyObject?.[valueKey as keyof Currency];
  }

  private filterAvailableAccountsAndSetSelectedValue(
    savingsAccountState: SavingsAccountStateModel,
    isToField: boolean,
  ) {
    let filteredList = this.dropdownListing.filter(
      // If either funding or withdrawal is enabled, include the account
      (account) => account.isFundingEnabled || account.isWithdrawalEnabled,
    );

    if (!isToField) {
      // we are from field need to not allow from to be an account where isWithdrawalEnabled is false
      filteredList = filteredList.filter((account) => account.isWithdrawalEnabled);
    } else {
      // we are to field need to not allow to to be an account where isFundingEnabled is false
      filteredList = filteredList.filter((account) => account.isFundingEnabled);
    }

    // Remove the account selected in the opposite field
    if (isToField) {
      filteredList = filteredList.filter(
        (account) =>
          account.accountId !==
          (savingsAccountState?.internalTransferFromAccountId || this.dropdownListing[0].accountId),
      );
    }
    this.dropdownListing = filteredList;
  }

  private getSelectedAccountByShowAllFlag(
    savingsAccountState: SavingsAccountStateModel,
    store: Store,
    isFromField: boolean,
  ): DropdownListingInterface {
    if (isFromField) {
      const internalTransferFromAccount = store.selectSnapshot(
        SavingsAccountState.getCurrentAccountDetails(
          savingsAccountState?.internalTransferFromAccountId ?? this.dropdownListing[0].accountId,
        ),
      );
      if (!savingsAccountState?.internalTransferFromAccountId)
        store.dispatch(new UpdateInternalTransferFromAccountId(internalTransferFromAccount?.accountId ?? ''));
      return this.getSavingAccountData(internalTransferFromAccount as SavingsAccountInfoType, 0);
    } else {
      const senderId = savingsAccountState?.internalTransferFromAccountId;
      const receiverId = savingsAccountState?.internalTransferToAccountId;
      const idsMatch = senderId === receiverId && senderId !== undefined && receiverId !== undefined;

      // find the default account id which does not match with internalTransferToAccountId so it should not update the same account id in the state
      const defaultAccountId = this.dropdownListing.find(
        (item) => item.accountId !== savingsAccountState?.internalTransferToAccountId,
      )?.accountId;

      const internalTransferToAccount = store.selectSnapshot(
        SavingsAccountState.getCurrentAccountDetails(
          !idsMatch
            ? savingsAccountState?.internalTransferToAccountId ?? (this.dropdownListing[0]?.accountId || '')
            : defaultAccountId || '',
        ),
      );

      // if internalTransferToAccountId is not present inside savingsAccountState and the idsMatch is true then it should update the receiver account details
      if (!savingsAccountState?.internalTransferToAccountId || idsMatch)
        store.dispatch(new UpdateInternalTransferToAccountId(internalTransferToAccount?.accountId ?? ''));
      return this.getSavingAccountData(internalTransferToAccount as SavingsAccountInfoType, 0);
    }
  }

  private setInternalTransferAccounts(
    store: Store,
    account: DropdownListingInterface,
    attri: UiZoneWidgetAttributeConfig,
  ) {
    const isFromField: boolean = attri['savingsInternalTransferAccountContext'] === 'from' ? true : false;
    if (isFromField) {
      store.dispatch(new UpdateInternalTransferFromAccountId(account.accountId));
    } else {
      store.dispatch(new UpdateInternalTransferToAccountId(account.accountId));
    }
  }
}
